import React, { useEffect, useState, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Star, ChevronDown, ChevronUp, BookOpen, Video, Smartphone, Infinity as InfinityIcon, BarChart2, AlertCircle, Brain, TrendingDown, ShieldCheck, Lock, Gift, Zap, XCircle, CheckCircle2, HelpCircle, Target, Monitor, FileText, ClipboardList, BadgeDollarSign } from 'lucide-react';
import { LegalModal } from '../components/ui/LegalModal';
// --- Meta Pixel type declaration ---
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}
// --- Types ---
type ModalType = 'privacy' | 'terms' | 'cookies' | null;
// --- Components ---
const CTAButton = ({
  text,
  className = '',
  fullWidth = false,
  subtext = '',
  isHero = false
}: {
  text: string;
  className?: string;
  fullWidth?: boolean;
  subtext?: string;
  isHero?: boolean;
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (isHero) {
      // Fire custom pixel event for hero scroll button
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'Click_Bajar_Página', {
          button_location: 'hero',
          destination: 'oferta-precio'
        });
      }
      // Direct instant scroll to the price section — no intermediate stops
      const el = document.getElementById('oferta-precio');
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    } else {
      // Fire pixel events for purchase buttons
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'AddToCart', {
          value: 6.36,
          currency: 'EUR'
        });
        window.fbq('track', 'InitiateCheckout', {
          value: 6.36,
          currency: 'EUR',
          num_items: 1
        });
      }
      window.location.href = 'https://www.claridaddirecta.com/cart/57813856682365:1';
    }
  };
  return <div className={`flex flex-col items-center ${fullWidth ? 'w-full' : ''}`} data-id="element-240">
      <a href={isHero ? '#oferta-precio' : 'https://www.claridaddirecta.com/cart/57813856682365:1'} onClick={handleClick} className={`inline-flex items-center justify-center bg-[#FFD700] text-[#003366] font-bold text-lg md:text-xl px-8 py-4 rounded-xl shadow-[0_4px_14px_0_rgba(255,215,0,0.39)] hover:shadow-[0_6px_20px_rgba(255,215,0,0.23)] hover:-translate-y-1 transition-all duration-200 cursor-pointer text-center ${fullWidth ? 'w-full' : ''} ${className}`} data-id="element-241">
        <span className="mr-2" data-id="element-242">▶️</span>
        {text}
      </a>
      {subtext && <span className="mt-3 text-xs md:text-sm text-gray-500 font-medium" data-id="element-243">
          {subtext}
        </span>}
    </div>;
};
const SectionHeading = ({
  children,
  className = '',
  centered = false
}: {
  children: React.ReactNode;
  className?: string;
  centered?: boolean;
}) => <h2 className={`${`text-3xl md:text-4xl font-bold text-[#003366] mb-6 leading-tight text-center ${className}`} text-[28px]`} data-id="element-244">
    {children}
  </h2>;
const AccordionItem = ({
  question,
  answer,
  isOpen,
  onClick
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) => {
  return <div className="border-b border-gray-200 last:border-0" data-id="element-245">
      <button onClick={onClick} className="w-full flex items-center justify-between py-5 text-left focus:outline-none group" data-id="element-246">
        <span className="text-lg font-semibold text-[#003366] group-hover:text-[#004080] transition-colors pr-4" data-id="element-247">
          {question}
        </span>
        {isOpen ? <ChevronUp className="h-5 w-5 text-[#003366] flex-shrink-0" data-id="element-248" /> : <ChevronDown className="h-5 w-5 text-gray-400 group-hover:text-[#003366] flex-shrink-0" data-id="element-249" />}
      </button>
      <AnimatePresence data-id="element-250">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-251">
            <p className="text-gray-600 pb-5 leading-relaxed" data-id="element-252">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function SalesLetterPage() {
  // ✅ TOGGLE DE BONOS — Cambiar a `true` para volver a mostrar los bonos
  const SHOW_BONUSES = false;
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(0);
  const [bonusExpanded, setBonusExpanded] = useState(false);
  useEffect(() => {
    // Force scroll to top on page load — prevents browser scroll restoration
    // or hash-based auto-scroll from bypassing the Hero section
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    // Initialize Meta Pixel
    if (typeof window.fbq !== 'function') {
      const script = document.createElement('script');
      script.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '1435718891550072');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(script);
      // Noscript fallback
      const noscript = document.createElement('noscript');
      const img = document.createElement('img');
      img.height = 1;
      img.width = 1;
      img.style.display = 'none';
      img.src = 'https://www.facebook.com/tr?id=1435718891550072&ev=PageView&noscript=1';
      noscript.appendChild(img);
      document.head.appendChild(noscript);
    } else {
      // Pixel already loaded, just fire PageView
      window.fbq('track', 'PageView');
    }
  }, []);
  return <div className="min-h-screen bg-white font-sans text-gray-700 selection:bg-[#FFD700]/30 pt-[44px]" data-id="element-253">
      {/* --- TOP BAR --- */}
      <div className="fixed top-0 left-0 right-0 h-[44px] bg-[#003366] z-50 flex items-center justify-center px-4 md:px-8 shadow-md" data-id="element-254">
        <div className="flex items-center gap-2" data-id="element-255">
          <span className="text-white text-sm font-medium" data-id="element-256">🔥 Hoy —</span>
          <span className="bg-[#FFD700] text-[#003366] text-xs font-bold px-3 py-1 rounded-full" data-id="element-257">
            90% OFF
          </span>
        </div>
      </div>

      {/* --- 1. HERO SECTION --- */}
      <section className="bg-white pt-12 pb-16 md:pt-20 md:pb-24 px-4 overflow-hidden" data-id="element-258">
        <div className="max-w-5xl mx-auto text-center" data-id="element-259">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-260">
            <span className="inline-block bg-[#FFD700]/20 text-[#003366] text-sm font-bold px-4 py-1 rounded-full mb-6 border border-[#FFD700]/50" data-id="element-261">
              Tu Primera Guía de Trading
            </span>

            {/* Headline — primary visual focus */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-[#003366] mb-8 tracking-tight leading-[1.15] md:leading-tight" data-id="element-262">
              Aprende Trading Desde Cero y Empieza a Entender el Mercado en
              Horas, No en Meses.
            </h1>

            {/* Product Mockup — immediately after headline */}
            <div className="relative max-w-2xl mx-auto mb-8" data-id="element-263">
              <div className="bg-gradient-to-br from-[#003366]/5 to-[#FFD700]/10 rounded-[28px] border border-gray-100 shadow-xl p-4 md:p-6 flex items-center justify-center overflow-hidden" style={{
              boxShadow: '0px 20px 40px rgba(0,0,0,0.08)'
            }} data-id="element-264">
                <img src="https://cdn.magicpatterns.com/uploads/pJeiWxAWrWCjYXFh8bhSzZ/heylloyd-image-1771946327690.png" alt="Trading Desde Cero - Guía Oficial con Ebook y Vídeos" className="w-full max-w-lg object-contain rounded-[20px]" data-id="element-265" />
              </div>
            </div>

            {/* Supporting text — below image, reduced visual weight */}
            <p className="md:text-xl text-gray-500 mb-4 max-w-3xl mx-auto font-normal text-[16px]" data-id="element-266">
              Una guía práctica y directa para principiantes que quieren
              entender el trading desde cero, sin confusiones ni tecnicismos.
            </p>
            <p className="text-sm md:text-base text-[#003366]/70 font-medium mb-10 max-w-2xl mx-auto" data-id="element-267">
              En pocas horas entenderás lo que la mayoría tarda meses en
              comprender.
            </p>

            <div className="flex flex-col items-center" data-id="element-268">
              <CTAButton text="QUIERO ACCEDER AHORA" isHero data-id="element-269" />

              {/* Social Proof & Guarantee */}
              <div className="mt-4 flex flex-col items-center gap-2" data-id="element-270">
                <div className="flex items-center gap-1 text-[#FFD700]" data-id="element-271">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-current" data-id="element-272" />)}
                  <span className="text-gray-500 text-xs md:text-sm font-medium ml-2" data-id="element-273">
                    4.9/5 — opiniones verificadas
                  </span>
                </div>
                <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm" data-id="element-274">
                  <ShieldCheck className="w-4 h-4 text-[#003366]" data-id="element-275" />
                  <span data-id="element-276">Garantía de 60 días — Riesgo cero</span>
                </div>
              </div>

              {/* Benefit Badges */}
              <div className="mt-6 flex flex-wrap justify-center gap-3" data-id="element-277">
                {['Ideal si empiezas desde cero', 'Explicado paso a paso', 'Sin tecnicismos complicados', 'Acceso inmediato'].map((badge, i) => <span key={i} className="inline-flex items-center gap-1.5 bg-[#003366]/5 border border-[#003366]/10 text-[#003366] text-xs font-semibold px-3 py-1.5 rounded-full" data-id="element-278">
                    <CheckCircle2 className="w-3.5 h-3.5" data-id="element-279" />
                    {badge}
                  </span>)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- 2. IDENTIFICATION BLOCK (NEW) --- */}
      <section id="section-identification" className="bg-[#F8FAFC] py-16 md:py-20 px-4 border-y border-gray-100" data-id="element-280">
        <div className="max-w-4xl mx-auto" data-id="element-281">
          <div className="text-center mb-12" data-id="element-282">
            <SectionHeading centered data-id="element-283">¿Te identificas con esto?</SectionHeading>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-284">
              Si alguna de estas situaciones te suena familiar, esta guía es
              exactamente lo que necesitas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-285">
            {[{
            icon: <HelpCircle className="w-6 h-6 text-red-500" data-id="element-286" />,
            text: 'Ves gráficos y no entiendes nada de lo que significan.'
          }, {
            icon: <AlertCircle className="w-6 h-6 text-orange-500" data-id="element-287" />,
            text: 'Abres operaciones sin saber realmente qué estás haciendo.'
          }, {
            icon: <Target className="w-6 h-6 text-blue-500" data-id="element-288" />,
            text: 'Sientes que todos entienden el trading menos tú.'
          }, {
            icon: <TrendingDown className="w-6 h-6 text-purple-500" data-id="element-289" />,
            text: 'No sabes por dónde empezar y cada día lo vas dejando.'
          }, {
            icon: <Brain className="w-6 h-6 text-green-500" data-id="element-290" />,
            text: 'Quieres entender antes de arriesgar ni un euro.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-start gap-4 hover:shadow-md transition-shadow ${i === 4 ? 'md:col-span-2 lg:col-span-1 lg:col-start-2' : ''}`} data-id="element-291">
                <div className="bg-gray-50 p-2 rounded-lg flex-shrink-0" data-id="element-292">
                  {item.icon}
                </div>
                <p className="text-gray-700 font-medium text-sm md:text-base leading-relaxed" data-id="element-293">
                  {item.text}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* --- 3. SOLUTION --- */}
      <section id="section-solution" className="bg-white py-16 md:py-24 px-4" data-id="element-294">
        <div className="max-w-5xl mx-auto" data-id="element-295">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-296">
            <motion.div className="flex-1" initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-297">
              <div className="inline-block bg-[#003366]/5 text-[#003366] font-bold px-4 py-1 rounded-full mb-4 text-sm text-center w-full" data-id="element-298">
                LA SOLUCIÓN DEFINITIVA
              </div>
              <SectionHeading className="mb-4" data-id="element-299">
                Por eso creamos "Tu Primera Guía de Trading"
              </SectionHeading>

              <div className="bg-blue-50 border-l-4 border-[#003366] p-4 mb-6 rounded-r-lg" data-id="element-300">
                <p className="text-[#003366] font-medium" data-id="element-301">
                  Guía estructurada como mini-curso con ebook + vídeos
                  explicativos.
                </p>
              </div>

              <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-302">
                Es una guía estructurada como un curso, que combina explicación
                escrita clara con vídeos prácticos para que entiendas cada
                concepto paso a paso.
              </p>

              {/* Solution Tags */}
              <div className="flex flex-wrap gap-3 mb-8" data-id="element-303">
                {['Sin tecnicismos', '100% Práctico', 'Vídeos HD', 'Acceso Inmediato'].map((tag, i) => <span key={i} className="bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1 rounded-md border border-gray-200" data-id="element-304">
                    {tag}
                  </span>)}
              </div>

              <ul className="space-y-4 mb-8" data-id="element-305">
                {['Explicaciones sin tecnicismos confusos', 'Vídeos cortos y directos al grano', 'Ejemplos visuales fáciles de entender'].map((item, i) => <li key={i} className="flex items-center text-gray-700" data-id="element-306">
                    <Check className="w-5 h-5 text-[#FFD700] mr-3 flex-shrink-0" strokeWidth={3} data-id="element-307" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            <motion.div className="flex-1 w-full" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-308">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-100 shadow-xl relative overflow-hidden" data-id="element-309">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#FFD700]/10 rounded-full blur-3xl" data-id="element-310"></div>
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#003366]/5 rounded-full blur-3xl" data-id="element-311"></div>

                <div className="relative z-10 flex flex-col gap-5" data-id="element-312">
                  {[{
                  icon: <BookOpen className="w-6 h-6 text-[#003366]" data-id="element-313" />,
                  title: 'Ebook Completo',
                  sub: 'Teoría paso a paso'
                }, {
                  icon: <Video className="w-6 h-6 text-[#003366]" data-id="element-314" />,
                  title: 'Vídeos Prácticos',
                  sub: 'Mira cómo se hace'
                }, {
                  icon: <BarChart2 className="w-6 h-6 text-[#003366]" data-id="element-315" />,
                  title: 'Ejemplos Reales',
                  sub: 'Gráficos analizados'
                }].map((item, i) => <div key={i} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4 hover:translate-x-1 transition-transform" data-id="element-316">
                      <div className="bg-blue-50 p-3 rounded-lg flex-shrink-0 text-[#003366]" data-id="element-317">
                        {item.icon}
                      </div>
                      <div data-id="element-318">
                        <div className="font-bold text-gray-900 text-lg" data-id="element-319">
                          {item.title}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-320">{item.sub}</div>
                      </div>
                    </div>)}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- 4. BENEFITS (LO QUE VAS A LOGRAR) --- */}
      <section id="section-benefits" className="bg-[#003366] py-16 md:py-24 px-4 text-white" data-id="element-321">
        <div className="max-w-6xl mx-auto" data-id="element-322">
          <div className="text-center mb-16" data-id="element-323">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-324">
              Lo que vas a entender
            </h2>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto" data-id="element-325">
              Todo explicado de forma simple, paso a paso, para que lo entiendas
              desde el primer día.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-326">
            {[{
            icon: '💰',
            title: 'Entender el Trading',
            desc: 'Qué es y cómo se gana dinero realmente.'
          }, {
            icon: '📏',
            title: 'Pips y Lotes',
            desc: 'Domina las unidades de medida del mercado.'
          }, {
            icon: '📈',
            title: 'Largo y Corto',
            desc: 'Aprende a operar en ambas direcciones.'
          }, {
            icon: '🛡️',
            title: 'Stop Loss y TP',
            desc: 'Protege tu capital y asegura ganancias.'
          }, {
            icon: '📝',
            title: 'Tipos de Órdenes',
            desc: 'Market, Limit, Stop... sabrás cuándo usarlas.'
          }, {
            icon: '🔍',
            title: 'Análisis Total',
            desc: 'Técnico, fundamental y sentimental.'
          }, {
            icon: '💻',
            title: 'Plataformas',
            desc: 'Conoce las herramientas esenciales.'
          }, {
            icon: '⚖️',
            title: 'Gestión de Riesgo',
            desc: 'La clave para no perder tu cuenta.'
          }].map((benefit, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors flex flex-col h-full" data-id="element-327">
                <div className="text-4xl mb-4" data-id="element-328">{benefit.icon}</div>
                <h3 className="font-bold text-lg mb-2 text-[#FFD700]" data-id="element-329">
                  {benefit.title}
                </h3>
                <p className="text-blue-100 text-sm leading-relaxed flex-1" data-id="element-330">
                  {benefit.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* --- AFTER BENEFITS: QUÉ PASARÁ DESPUÉS --- */}
      <section id="section-after-benefits" className="bg-white py-14 px-4 border-b border-gray-100" data-id="element-331">
        <div className="max-w-2xl mx-auto text-center" data-id="element-332">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-333">
            <span className="inline-block bg-[#FFD700]/20 text-[#003366] text-sm font-bold px-4 py-1 rounded-full mb-6 border border-[#FFD700]/50" data-id="element-334">
              El resultado
            </span>
            <SectionHeading centered data-id="element-335">
              Qué pasará después de esta guía
            </SectionHeading>
            <div className="flex flex-col gap-4 mt-8 text-left max-w-md mx-auto" data-id="element-336">
              {['Entenderás qué ves en un gráfico y por qué se mueve el precio.', 'Sabrás qué significa comprar o vender y cuándo tiene sentido hacerlo.', 'Dejarás de sentirte perdido viendo trading y empezarás a entender.'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-337">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-338" />
                  <p className="text-gray-700 font-medium" data-id="element-339">{item}</p>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- PARA QUIÉN ES / NO ES --- */}
      <section id="section-for-who" className="bg-[#F8FAFC] py-14 px-4 border-b border-gray-100" data-id="element-340">
        <div className="max-w-3xl mx-auto" data-id="element-341">
          <div className="text-center mb-10" data-id="element-342">
            <SectionHeading centered data-id="element-343">¿Es esta guía para ti?</SectionHeading>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-344">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-green-100" data-id="element-345">
              <h3 className="font-bold text-[#003366] text-lg mb-4 flex items-center gap-2" data-id="element-346">
                <CheckCircle2 className="w-5 h-5 text-green-500" data-id="element-347" />
                Esta guía es para ti si:
              </h3>
              <ul className="space-y-3" data-id="element-348">
                {['Empiezas desde cero y quieres una base sólida.', 'Quieres entender el trading antes de invertir dinero.', 'Buscas claridad sin tecnicismos ni complicaciones.'].map((item, i) => <li key={i} className="flex items-start gap-2 text-gray-700 text-sm" data-id="element-349">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-350" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-red-100" data-id="element-351">
              <h3 className="font-bold text-[#003366] text-lg mb-4 flex items-center gap-2" data-id="element-352">
                <XCircle className="w-5 h-5 text-red-400" data-id="element-353" />
                No es para ti si:
              </h3>
              <ul className="space-y-3" data-id="element-354">
                {['Buscas ganancias rápidas sin esfuerzo.', 'Quieres señales mágicas para hacerte rico.'].map((item, i) => <li key={i} className="flex items-start gap-2 text-gray-500 text-sm" data-id="element-355">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" data-id="element-356" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- 6. CURRICULUM (QUÉ VAS A APRENDER) --- */}
      <section className="bg-[#F8FAFC] py-16 md:py-24 px-4 border-y border-gray-100" data-id="element-357">
        <div className="max-w-4xl mx-auto" data-id="element-358">
          <div className="text-center mb-12" data-id="element-359">
            <SectionHeading centered data-id="element-360">¿Qué vas a aprender?</SectionHeading>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto" data-id="element-361">
              Un recorrido completo desde lo más básico hasta tu primera
              operación.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start" data-id="element-362">
            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-200" data-id="element-363">
              <img src="https://cdn.magicpatterns.com/uploads/nfoi8HJTP6dFZJs44CUFDX/sssss.png" alt="Trading learning path" className="w-full h-full object-cover min-h-[300px]" data-id="element-364" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#003366]/80 to-transparent flex items-end p-6" data-id="element-365">
                <div className="text-white" data-id="element-366">
                  <div className="font-bold text-xl mb-1" data-id="element-367">
                    Método Paso a Paso
                  </div>
                  <div className="text-sm opacity-90" data-id="element-368">
                    Estructurado para facilitar tu aprendizaje
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-369">
              {[{
              title: 'Módulo 1: Fundamentos del Trading',
              content: 'Entenderás qué es realmente el trading, cómo funciona el mercado y la mentalidad correcta para empezar.'
            }, {
              title: 'Módulo 2: Análisis Técnico Básico',
              content: 'Aprenderás a leer gráficos, identificar tendencias y entender qué te dicen las velas japonesas.'
            }, {
              title: 'Módulo 3: Gestión de Riesgo',
              content: 'El módulo más importante. Cómo proteger tu capital y cuánto arriesgar en cada operación para no quemar tu cuenta.'
            }, {
              title: 'Módulo 4: Tu Primera Estrategia',
              content: 'Configuración de indicadores básicos y reglas claras para entrar y salir del mercado.'
            }, {
              title: 'Módulo 5: Plataformas y Herramientas',
              content: 'Guía práctica para configurar tu entorno de trabajo y usar las herramientas profesionales.'
            }].map((module, i) => <div key={i} className="border-b border-gray-100 last:border-0" data-id="element-370">
                  <button onClick={() => setOpenAccordionIndex(openAccordionIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors" data-id="element-371">
                    <div className="flex items-center gap-3" data-id="element-372">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#003366] text-white text-xs font-bold" data-id="element-373">
                        {i + 1}
                      </span>
                      <span className="font-semibold text-[#003366]" data-id="element-374">
                        {module.title}
                      </span>
                    </div>
                    {openAccordionIndex === i ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-375" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-376" />}
                  </button>
                  <AnimatePresence data-id="element-377">
                    {openAccordionIndex === i && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden bg-gray-50/50" data-id="element-378">
                        <p className="p-5 pt-0 text-sm text-gray-600 leading-relaxed pl-14" data-id="element-379">
                          {module.content}
                        </p>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* --- BONOS TRAS CURRICULUM --- */}
      {SHOW_BONUSES && <section className="bg-[#001f4d] py-14 md:py-20 px-4 relative overflow-hidden" data-id="element-380">
          <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(#FFD700_1px,transparent_1px)] [background-size:28px_28px]" data-id="element-381"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FFD700] to-transparent opacity-40" data-id="element-382"></div>
          <div className="max-w-4xl mx-auto relative z-10" data-id="element-383">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-384">
              {/* Title — mobile: centered, larger, full-width with safe padding */}
              <div className="flex flex-col items-center text-center mb-10 px-4 sm:px-0" data-id="element-385">
                {/* 1. Badge */}
                <span className="inline-block bg-gradient-to-r from-[#003366] to-[#004080] text-[#FFD700] text-[10px] font-extrabold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full border border-[#FFD700]/30 mb-5" data-id="element-386">
                  Bonos Exclusivos
                </span>

                {/* 2. Título principal */}
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight mb-4 max-w-2xl" data-id="element-387">
                  Hoy recibes 3 Bonos Exclusivos —{' '}
                  <span className="text-[#FFD700]" data-id="element-388">GRATIS</span>{' '}
                  <span className="text-white/70 font-bold" data-id="element-389">
                    (Valor total 141€)
                  </span>
                </h2>

                {/* 3. Texto explicativo */}
                <p className="text-blue-200/70 text-sm md:text-base leading-relaxed max-w-md" data-id="element-390">
                  Además del acceso a la guía, recibes herramientas prácticas
                  diseñadas para ayudarte a empezar a operar sin confusión desde
                  el primer día.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8" data-id="element-391">
                {[{
              number: '1',
              icon: <Monitor className="w-4 h-4 text-[#FFD700]" data-id="element-392" />,
              title: 'Cuenta Demo Express',
              label: 'ACCESO INMEDIATO',
              benefit: 'Practica sin arriesgar dinero real',
              image: 'https://cdn.magicpatterns.com/uploads/7MZ731ZbGN2iSbdZb5z8qT/Bono_1_Demo_.png'
            }, {
              number: '2',
              icon: <FileText className="w-4 h-4 text-[#FFD700]" data-id="element-393" />,
              title: 'Plantilla Operativa Flash',
              label: 'PLANTILLA LISTA',
              benefit: 'Tu hoja de ruta para cada operación',
              image: 'https://cdn.magicpatterns.com/uploads/urvPVebhYTSFF8tSQLjDPP/Bono_2.png'
            }, {
              number: '3',
              icon: <ClipboardList className="w-4 h-4 text-[#FFD700]" data-id="element-394" />,
              title: 'Checklist Anti-Bloqueo',
              label: 'CHECKLIST RÁPIDO',
              benefit: 'Nunca más te quedes paralizado',
              image: 'https://cdn.magicpatterns.com/uploads/vky48WXX4baSSovq9xrZgb/Bono_3_.png'
            }].map((b, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.12,
              duration: 0.4
            }} className="flex flex-col items-center gap-3" data-id="element-395">
                    {/* Image container: full-width on mobile (up to 420px), 200px cap on desktop */}
                    <div className="relative w-full max-w-[420px] md:max-w-[200px] mx-auto px-3 md:px-0 pb-2 md:pb-0" data-id="element-396">
                      <img src={b.image} alt={b.title} className="w-full h-auto md:aspect-[3/4] md:object-cover md:object-top object-contain block rounded-xl border border-white/10 shadow-lg" data-id="element-397" />
                      <div className="absolute top-2 left-5 md:left-2 w-7 h-7 bg-[#003366] border-2 border-[#FFD700] rounded-full flex items-center justify-center" data-id="element-398">
                        <span className="text-[#FFD700] text-[10px] font-extrabold" data-id="element-399">
                          #{b.number}
                        </span>
                      </div>
                      <div className="absolute top-2 right-5 md:right-2 bg-[#FFD700] text-[#003366] text-[9px] font-extrabold px-2 py-0.5 rounded-full shadow" data-id="element-400">
                        $47
                      </div>
                    </div>
                    {/* Text block — benefit-focused, not title repetition */}
                    <div className="text-center mt-1" data-id="element-401">
                      <div className="flex items-center justify-center gap-1.5 mb-1.5" data-id="element-402">
                        <span className="text-[#FFD700] text-[18px] md:text-[10px] font-semibold md:font-bold uppercase tracking-[0.5px] md:tracking-wider leading-snug" data-id="element-403">
                          🎁 Bono #{b.number} — {b.label}
                        </span>
                      </div>
                      <p className="text-white/80 text-xs leading-snug mb-1.5 px-2" data-id="element-404">
                        {b.benefit}
                      </p>
                      <span className="text-green-400 text-xs font-bold flex items-center justify-center gap-1" data-id="element-405">
                        <CheckCircle2 className="w-3 h-3" data-id="element-406" />
                        Incluido gratis hoy
                      </span>
                    </div>
                  </motion.div>)}
              </div>

              {/* --- RESUMEN RÁPIDO --- */}
              <motion.div initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="mt-6 max-w-sm mx-auto bg-gradient-to-br from-[#002a66] to-[#003d80] border border-white/10 rounded-2xl px-6 py-5" data-id="element-407">
                <p className="text-white font-semibold text-sm uppercase tracking-widest mb-5 opacity-80 text-center" data-id="element-408">
                  Resumen rápido
                </p>
                <ul className="flex flex-col" style={{
              gap: '16px'
            }} data-id="element-409">
                  {[<>
                      Valor total de los bonos:{' '}
                      <span className="font-semibold text-white" data-id="element-410">141€</span>
                    </>, <>
                      Hoy los recibes{' '}
                      <span className="font-semibold text-green-400" data-id="element-411">
                        GRATIS
                      </span>{' '}
                      con tu acceso
                    </>, <>
                      Se desbloquean automáticamente al entrar a la formación
                    </>].map((line, i) => <li key={i} className="flex items-center gap-3 text-blue-100 text-sm" style={{
                lineHeight: '1.45'
              }} data-id="element-412">
                      <Check className="text-green-400 flex-shrink-0" style={{
                  width: '18px',
                  height: '18px',
                  minWidth: '18px'
                }} strokeWidth={2.5} data-id="element-413" />
                      <span data-id="element-414">{line}</span>
                    </li>)}
                </ul>
              </motion.div>
            </motion.div>
          </div>
        </section>}

      {/* --- 8. TESTIMONIALS --- */}
      <section className="bg-[#F8FAFC] py-16 md:py-24 px-4 border-y border-gray-100" data-id="element-415">
        <div className="max-w-6xl mx-auto" data-id="element-416">
          <div className="text-center mb-12" data-id="element-417">
            <SectionHeading centered data-id="element-418">
              Lo que dicen quienes ya dieron sus primeros pasos en trading
            </SectionHeading>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-419">
            {[{
            text: 'Siempre veía trading en internet pero no entendía absolutamente nada. Esta guía fue la primera vez que alguien me explicó todo desde cero y sin palabras raras. En una tarde ya entendía lo que estaba viendo en los gráficos.',
            author: 'Laura M.',
            age: '32 años',
            avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face&auto=format&q=80'
          }, {
            text: 'Pensaba que el trading era solo para expertos, pero esta guía lo simplifica muchísimo. Ahora sé qué significa comprar, vender y cómo no entrar al mercado sin sentido. Me dio una base clara que nunca había encontrado.',
            author: 'Javier R.',
            age: '40 años',
            avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face&auto=format&q=80'
          }, {
            text: 'Lo mejor no fue aprender estrategias, fue dejar de sentirme perdido. Por fin entiendo conceptos básicos como riesgo y entradas. Ahora veo el trading con tranquilidad y no con confusión.',
            author: 'Marta G.',
            age: '28 años',
            avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face&auto=format&q=80'
          }].map((t, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white border-t-4 border-[#FFD700] rounded-2xl shadow-lg p-8 flex flex-col h-full" data-id="element-420">
                <div className="flex text-[#FFD700] mb-4" data-id="element-421">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-current" data-id="element-422" />)}
                </div>
                <p className="text-gray-600 italic mb-6 flex-1 leading-relaxed text-sm md:text-base" data-id="element-423">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100" data-id="element-424">
                  <img src={t.avatar} alt={t.author} className="w-10 h-10 rounded-full object-cover flex-shrink-0" data-id="element-425" />
                  <div data-id="element-426">
                    <div className="font-bold text-[#003366] text-sm" data-id="element-427">
                      {t.author}
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-428">{t.age}</div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* --- 5. PRICE/OFFER BLOCK --- */}
      <section id="oferta-precio" className="bg-white py-16 md:py-24 px-4 relative overflow-hidden" data-id="element-429">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#003366_0.5px,transparent_0.5px)] [background-size:24px_24px] opacity-[0.03]" data-id="element-430"></div>
        <div className="max-w-lg mx-auto relative z-10" data-id="element-431">
          <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-white rounded-3xl overflow-hidden text-center shadow-[0_20px_50px_rgba(0,51,102,0.15)] border border-gray-100" data-id="element-432">
            {/* --- 1. BADGE SUPERIOR --- */}
            <div className="bg-[#003366]/5 border-b border-[#003366]/8 px-8 py-3 text-center" data-id="element-433">
              <span className="text-[#003366] text-[11px] font-bold uppercase tracking-[0.15em]" data-id="element-434">
                Oferta especial para principiantes
              </span>
            </div>

            <div className="px-8 md:px-12 pt-8 pb-10" data-id="element-435">
              {/* --- 2. TÍTULO PRINCIPAL --- */}
              <div className="mb-6 text-center" data-id="element-436">
                <h3 className="text-xl font-extrabold text-[#003366] uppercase tracking-tight mb-1" data-id="element-437">
                  Tu Primera Guía de Trading
                </h3>
                <p className="text-gray-400 text-sm leading-snug" data-id="element-438">
                  Aprende trading desde cero sin complicaciones.
                </p>
              </div>

              {/* --- DIVIDER --- */}
              <div className="border-t border-gray-100 mb-6" data-id="element-439" />

              {/* --- 3. ESTÁS ACCEDIENDO HOY A --- */}
              <div className="text-left mb-6" data-id="element-440">
                <p className="text-xs font-extrabold text-[#003366] uppercase tracking-widest mb-3" data-id="element-441">
                  Estás accediendo hoy a:
                </p>
                <ul className="space-y-2.5" data-id="element-442">
                  {['Guía clara para entender el mercado desde cero', 'Principios esenciales antes de usar estrategias', 'Cómo practicar sin arriesgar dinero real', 'Base sólida para empezar en trading sin confusión'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-443">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#003366]/8 flex items-center justify-center mt-0.5" data-id="element-444">
                        <Check className="w-3 h-3 text-[#003366]" strokeWidth={3} data-id="element-445" />
                      </div>
                      <span className="text-gray-700 text-sm leading-snug" data-id="element-446">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>

              {/* --- 4. BONOS --- */}
              {SHOW_BONUSES && <div className="bg-[#001f4d] rounded-2xl p-5 mb-6 text-left" data-id="element-447">
                  <div className="mb-4" data-id="element-448">
                    <div className="flex items-center gap-2 mb-1" data-id="element-449">
                      <Gift className="w-4 h-4 text-[#FFD700] flex-shrink-0" data-id="element-450" />
                      <span className="text-[#FFD700] text-xs font-bold uppercase tracking-wider" data-id="element-451">
                        Además, hoy recibes gratis:
                      </span>
                    </div>
                    <p className="text-blue-200/60 text-[11px] pl-6" data-id="element-452">
                      Valor total de bonos: 141€
                    </p>
                  </div>
                  <div className="flex flex-col gap-3" data-id="element-453">
                    {[{
                  number: '1',
                  title: 'Cuenta Demo Express',
                  benefit: 'Practica sin arriesgar dinero real',
                  image: 'https://cdn.magicpatterns.com/uploads/7MZ731ZbGN2iSbdZb5z8qT/Bono_1_Demo_.png'
                }, {
                  number: '2',
                  title: 'Plantilla Operativa Flash',
                  benefit: 'Tu hoja de ruta para cada operación',
                  image: 'https://cdn.magicpatterns.com/uploads/urvPVebhYTSFF8tSQLjDPP/Bono_2.png'
                }, {
                  number: '3',
                  title: 'Checklist Anti-Bloqueo',
                  benefit: 'Evita los errores más comunes al empezar',
                  image: 'https://cdn.magicpatterns.com/uploads/vky48WXX4baSSovq9xrZgb/Bono_3_.png'
                }].map((b, i) => <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl p-2.5 border border-white/10" data-id="element-454">
                        <div className="relative flex-shrink-0 w-14 h-[74px]" data-id="element-455">
                          <img src={b.image} alt={b.title} className="w-full h-full object-cover object-top rounded-lg border border-white/10" data-id="element-456" />
                          <div className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-[#003366] border border-[#FFD700] rounded-full flex items-center justify-center" data-id="element-457">
                            <span className="text-[#FFD700] text-[9px] font-extrabold" data-id="element-458">
                              #{b.number}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-459">
                          <p className="text-white text-sm font-bold leading-tight" data-id="element-460">
                            {b.title}
                          </p>
                          <p className="text-blue-200/70 text-xs leading-snug mt-0.5" data-id="element-461">
                            {b.benefit}
                          </p>
                        </div>
                        <span className="flex-shrink-0 text-green-400 text-[10px] font-bold bg-green-400/10 border border-green-400/20 px-2 py-0.5 rounded-full" data-id="element-462">
                          GRATIS
                        </span>
                      </div>)}
                  </div>

                  {/* --- LEER MÁS toggle --- */}
                  <button onClick={() => setBonusExpanded(!bonusExpanded)} className="mt-4 w-full flex items-center justify-center gap-1.5 text-[#FFD700]/80 hover:text-[#FFD700] text-xs font-semibold transition-colors focus:outline-none" data-id="element-463">
                    {bonusExpanded ? <>
                        Cerrar detalle
                        <ChevronUp className="w-3.5 h-3.5" data-id="element-464" />
                      </> : <>
                        Leer más
                        <ChevronDown className="w-3.5 h-3.5" data-id="element-465" />
                      </>}
                  </button>

                  {/* --- EXPANDED BONUS DETAIL --- */}
                  <AnimatePresence data-id="element-466">
                    {bonusExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-467">
                        <div className="mt-4 pt-4 border-t border-white/10 flex flex-col gap-6" data-id="element-468">
                          {[{
                      number: '1',
                      label: 'ACCESO INMEDIATO',
                      title: 'Cuenta Demo Express',
                      benefit: 'Practica sin arriesgar dinero real',
                      image: 'https://cdn.magicpatterns.com/uploads/7MZ731ZbGN2iSbdZb5z8qT/Bono_1_Demo_.png'
                    }, {
                      number: '2',
                      label: 'PLANTILLA LISTA',
                      title: 'Plantilla Operativa Flash',
                      benefit: 'Tu hoja de ruta para cada operación',
                      image: 'https://cdn.magicpatterns.com/uploads/urvPVebhYTSFF8tSQLjDPP/Bono_2.png'
                    }, {
                      number: '3',
                      label: 'CHECKLIST RÁPIDO',
                      title: 'Checklist Anti-Bloqueo',
                      benefit: 'Nunca más te quedes paralizado',
                      image: 'https://cdn.magicpatterns.com/uploads/vky48WXX4baSSovq9xrZgb/Bono_3_.png'
                    }].map((b, i) => <div key={i} className="flex flex-col items-center gap-3" data-id="element-469">
                              <div className="relative w-full max-w-[280px] mx-auto" data-id="element-470">
                                <img src={b.image} alt={b.title} className="w-full h-auto object-contain block rounded-xl border border-white/10 shadow-lg" data-id="element-471" />
                                <div className="absolute top-2 left-2 w-7 h-7 bg-[#003366] border-2 border-[#FFD700] rounded-full flex items-center justify-center" data-id="element-472">
                                  <span className="text-[#FFD700] text-[10px] font-extrabold" data-id="element-473">
                                    #{b.number}
                                  </span>
                                </div>
                                <div className="absolute top-2 right-2 bg-[#FFD700] text-[#003366] text-[9px] font-extrabold px-2 py-0.5 rounded-full shadow" data-id="element-474">
                                  $47
                                </div>
                              </div>
                              <div className="text-center" data-id="element-475">
                                <p className="text-[#FFD700] text-[11px] font-bold uppercase tracking-wider mb-1" data-id="element-476">
                                  Bono #{b.number} — {b.label}
                                </p>
                                <p className="text-white font-semibold text-sm mb-1" data-id="element-477">
                                  {b.title}
                                </p>
                                <p className="text-white/70 text-xs leading-snug mb-2" data-id="element-478">
                                  {b.benefit}
                                </p>
                                <span className="text-green-400 text-xs font-bold flex items-center justify-center gap-1" data-id="element-479">
                                  <CheckCircle2 className="w-3 h-3" data-id="element-480" />
                                  Incluido gratis hoy
                                </span>
                              </div>
                            </div>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>}

              {/* --- DIVIDER --- */}
              <div className="border-t border-gray-100 mb-6" data-id="element-481" />

              {/* --- 5. PRECIO --- */}
              <div className="mb-5 text-center" data-id="element-482">
                <div className="flex items-center justify-center gap-2 mb-2" data-id="element-483">
                  <span className="text-base text-gray-400 line-through font-medium" data-id="element-484">
                    67 €
                  </span>
                  <span className="text-green-600 text-[11px] font-bold bg-green-50 border border-green-200 px-2 py-0.5 rounded-full" data-id="element-485">
                    90% OFF
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 mb-5" data-id="element-486">
                  <span className="text-6xl md:text-7xl font-extrabold text-[#003366] tracking-tighter" data-id="element-487">
                    6,36
                  </span>
                  <span className="flex flex-col items-start" data-id="element-488">
                    <span className="text-2xl font-bold text-[#003366]" data-id="element-489">€</span>
                    <span className="text-xs text-gray-500 font-medium uppercase" data-id="element-490">
                      Pago único
                    </span>
                  </span>
                </div>

                {/* --- 6. BENEFICIOS DE COMPRA --- */}
                <ul className="space-y-1.5 text-left mb-4 max-w-xs mx-auto" data-id="element-491">
                  {['Acceso inmediato tras el pago', 'Sin suscripciones', 'Acceso de por vida'].map((item, i) => <li key={i} className="flex items-center gap-2 text-gray-600 text-sm" data-id="element-492">
                      <Check className="w-3.5 h-3.5 text-[#003366] flex-shrink-0" strokeWidth={3} data-id="element-493" />
                      {item}
                    </li>)}
                </ul>

                {/* --- 7. MICRO COPY --- */}
                <p className="text-gray-400 text-xs italic mb-6" data-id="element-494">
                  Menos que un café, pero una habilidad que puedes usar siempre.
                </p>
              </div>

              {/* --- 8. CTA --- */}
              <CTAButton text="QUIERO ACCEDER AHORA" fullWidth className="w-full shadow-xl" data-id="element-495" />
              <p className="mt-3 text-center text-xs text-gray-400" data-id="element-496">
                Acceso inmediato tras el pago — sin procesos complicados.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- 7. GUARANTEE BLOCK (NEW) --- */}
      <section className="bg-white py-16 px-4" data-id="element-497">
        <div className="max-w-3xl mx-auto" data-id="element-498">
          <div className="bg-gradient-to-br from-[#FFFBEB] to-white border border-[#FFD700]/30 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-8 shadow-lg relative overflow-hidden" data-id="element-499">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFD700]/10 rounded-bl-full -mr-10 -mt-10" data-id="element-500"></div>
            <div className="flex-shrink-0" data-id="element-501">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-md border border-[#FFD700]/20" data-id="element-502">
                <ShieldCheck className="w-12 h-12 text-[#FFD700]" data-id="element-503" />
              </div>
            </div>
            <div className="text-center md:text-left flex-1" data-id="element-504">
              <h3 className="text-2xl font-bold text-[#003366] mb-3" data-id="element-505">
                Garantía Total de 60 Días
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4" data-id="element-506">
                Puedes probar todo el contenido sin ningún riesgo. Si sientes
                que la guía no te aporta el valor que esperabas o no es para ti,
                puedes solicitar el reembolso completo de tu dinero.
              </p>
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#003366]" data-id="element-507">
                <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-508" />
                <span data-id="element-509">Sin preguntas. Sin letra pequeña.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- 9. WHAT'S INCLUDED (ACCESS) --- */}
      <section className="bg-white py-16 md:py-24 px-4" data-id="element-510">
        <div className="max-w-5xl mx-auto" data-id="element-511">
          <div className="text-center mb-12" data-id="element-512">
            <SectionHeading centered data-id="element-513">¿Qué incluye tu acceso?</SectionHeading>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-514">
            {[{
            emoji: '⚡',
            title: 'Acceso inmediato',
            desc: 'Empieza ahora mismo'
          }, {
            emoji: '♾️',
            title: 'Acceso de por vida',
            desc: 'Sin pagos recurrentes'
          }, {
            emoji: '📱',
            title: 'Compatible móvil y PC',
            desc: 'Aprende donde quieras'
          }, {
            emoji: '🔄',
            title: 'Actualizaciones',
            desc: 'Incluidas gratis'
          }, {
            emoji: '📘',
            title: 'Ebook completo',
            desc: 'Guía en PDF'
          }, {
            emoji: '🎥',
            title: 'Vídeos explicativos',
            desc: 'Material práctico'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="bg-white border border-gray-100 rounded-xl p-6 text-center hover:border-[#003366]/30 hover:shadow-md transition-all duration-300 group" data-id="element-515">
                <div className="text-3xl mb-3 group-hover:scale-110 transition-transform duration-300" data-id="element-516">
                  {item.emoji}
                </div>
                <h3 className="font-bold text-[#003366] text-base mb-1" data-id="element-517">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-sm" data-id="element-518">{item.desc}</p>
              </motion.div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-519">
            <CTAButton text="Sí, quiero acceso inmediato" data-id="element-520" />
          </div>
        </div>
      </section>

      {/* --- 10. FAQ --- */}
      <section className="bg-[#F8FAFC] py-16 md:py-24 px-4 border-y border-gray-100" data-id="element-521">
        <div className="max-w-3xl mx-auto" data-id="element-522">
          <div className="text-center mb-12" data-id="element-523">
            <SectionHeading centered data-id="element-524">Preguntas Frecuentes</SectionHeading>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-525">
            <AccordionItem question="¿Necesito experiencia previa?" answer="No. Esta guía está diseñada desde cero para personas que nunca han operado en mercados financieros. Empezamos desde los conceptos más básicos." isOpen={openAccordionIndex === 10} onClick={() => setOpenAccordionIndex(openAccordionIndex === 10 ? null : 10)} data-id="element-526" />
            <AccordionItem question="¿Es realmente para principiantes?" answer="Sí, absolutamente. El lenguaje es claro, sin tecnicismos innecesarios, y cada concepto se explica con ejemplos prácticos." isOpen={openAccordionIndex === 11} onClick={() => setOpenAccordionIndex(openAccordionIndex === 11 ? null : 11)} data-id="element-527" />
            <AccordionItem question="¿Cuándo recibo acceso?" answer="El acceso es inmediato tras el pago. Recibirás un email con el enlace de descarga en cuestión de minutos." isOpen={openAccordionIndex === 12} onClick={() => setOpenAccordionIndex(openAccordionIndex === 12 ? null : 12)} data-id="element-528" />
            <AccordionItem question="¿Incluye vídeos?" answer="Sí. Además del ebook completo, incluye vídeos explicativos en cada módulo para reforzar el aprendizaje de forma visual." isOpen={openAccordionIndex === 13} onClick={() => setOpenAccordionIndex(openAccordionIndex === 13 ? null : 13)} data-id="element-529" />
            <AccordionItem question="¿Cuánto tiempo tengo acceso?" answer="Acceso de por vida. Una vez que lo adquieres, es tuyo para siempre, incluyendo futuras actualizaciones." isOpen={openAccordionIndex === 14} onClick={() => setOpenAccordionIndex(openAccordionIndex === 14 ? null : 14)} data-id="element-530" />
          </div>
        </div>
      </section>

      {/* --- 11. FINAL CTA (CIERRE) --- */}
      <section className="bg-[#003366] py-20 px-4 text-center relative overflow-hidden" data-id="element-531">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFD700_1px,transparent_1px)] [background-size:20px_20px]" data-id="element-532"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-533">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-534">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-535">
              Tu claridad en trading empieza hoy.
            </h2>
            <p className="text-blue-200 text-lg mb-8 max-w-xl mx-auto" data-id="element-536">
              Deja de sentirte perdido. En pocas horas tendrás la base que
              necesitas para empezar con confianza.
            </p>

            <div className="flex items-center justify-center gap-2 mb-8" data-id="element-537">
              <span className="text-5xl md:text-6xl font-bold text-white" data-id="element-538">
                6,36 €
              </span>
            </div>

            <CTAButton text="QUIERO ACCEDER AHORA" className="text-xl px-10 py-5" data-id="element-539" />

            <div className="mt-4 text-center text-xs text-blue-200/60" data-id="element-540">
              ✅ Acceso inmediato &nbsp;·&nbsp; ✅ Pago único &nbsp;·&nbsp; ✅
              Acceso de por vida
            </div>

            <div className="mt-6 flex flex-col items-center gap-3" data-id="element-541">
              <div className="flex items-center gap-1 text-[#FFD700]" data-id="element-542">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-5 h-5 fill-current" data-id="element-543" />)}
              </div>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-blue-200/80" data-id="element-544">
                <span className="flex items-center gap-1" data-id="element-545">
                  <ShieldCheck className="h-4 w-4" data-id="element-546" /> Garantía de 60 días
                </span>
                <span className="flex items-center gap-1" data-id="element-547">
                  <Lock className="h-4 w-4" data-id="element-548" /> Pago 100% seguro
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="bg-gray-50 border-t border-gray-200 py-10 px-4" data-id="element-549">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-550">
          <p className="text-gray-500 text-[12px]" data-id="element-551">
            © 2026 Claridad Directa. Todos los derechos reservados.
          </p>
          <div className="flex flex-wrap justify-center gap-[10px]" data-id="element-552">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-500 hover:text-[#003366] text-sm transition-colors" data-id="element-553">
              Política de Privacidad
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-gray-500 hover:text-[#003366] text-sm transition-colors" data-id="element-554">
              Términos y Condiciones
            </button>
            <button onClick={() => setActiveModal('cookies')} className="text-gray-500 hover:text-[#003366] text-sm transition-colors" data-id="element-555">
              Cookies
            </button>
          </div>
        </div>
      </footer>

      {/* --- LEGAL MODALS --- */}
      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-556">
        <p className="text-white font-semibold" data-id="element-557">
          Última actualización: junio 2025
        </p>
        <p data-id="element-558">
          En cumplimiento con la normativa vigente en materia de protección de
          datos personales, te informamos sobre cómo tratamos tu información al
          adquirir nuestro producto digital{' '}
          <span className="text-white font-medium" data-id="element-559">
            Tu Primera Guía de Trading
          </span>
          .
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-560">
          1. Datos que recogemos
        </h3>
        <p data-id="element-561">
          Únicamente recogemos tu{' '}
          <span className="text-white" data-id="element-562">dirección de correo electrónico</span>,
          necesaria para la entrega del producto digital (ebook en formato PDF y
          acceso a vídeos). No solicitamos datos bancarios directamente; el pago
          se procesa a través de la pasarela segura de Shopify.
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-563">
          2. Finalidad del tratamiento
        </h3>
        <p data-id="element-564">
          Tu email se utiliza exclusivamente para: enviar el enlace de descarga
          del ebook y, en caso necesario, comunicaciones relacionadas con tu
          compra (soporte, actualizaciones del producto).
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-565">3. Cesión a terceros</h3>
        <p data-id="element-566">
          <span className="text-white font-medium" data-id="element-567">
            No compartimos, vendemos ni cedemos tus datos personales a terceros
          </span>{' '}
          bajo ninguna circunstancia, salvo obligación legal.
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-568">
          4. Conservación de datos
        </h3>
        <p data-id="element-569">
          Tus datos se conservarán mientras sean necesarios para la finalidad
          indicada y durante los plazos legales aplicables.
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-570">5. Tus derechos</h3>
        <p data-id="element-571">
          Puedes ejercer tus derechos de acceso, rectificación, supresión y
          oposición contactándonos a través de nuestro email de soporte.
        </p>
      </LegalModal>

      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-572">
        <p className="text-white font-semibold" data-id="element-573">
          Última actualización: junio 2025
        </p>
        <p data-id="element-574">
          Al adquirir el producto digital{' '}
          <span className="text-white font-medium" data-id="element-575">
            Tu Primera Guía de Trading
          </span>
          , aceptas los siguientes términos y condiciones de uso:
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-576">1. Uso del producto</h3>
        <p data-id="element-577">
          Este producto es para{' '}
          <span className="text-white" data-id="element-578">uso personal y exclusivo</span> del
          comprador. Queda estrictamente prohibida su redistribución, reventa,
          copia o publicación sin autorización expresa por escrito del autor.
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-579">
          2. Exención de responsabilidad
        </h3>
        <p data-id="element-580">
          El contenido es{' '}
          <span className="text-white font-medium" data-id="element-581">
            exclusivamente educativo e informativo
          </span>
          . No constituye asesoramiento financiero ni recomendación de trading.
          El usuario asume todos los riesgos derivados de operar en mercados
          financieros. Los resultados pasados no garantizan resultados futuros.
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-582">3. Derechos de autor</h3>
        <p data-id="element-583">
          Todo el contenido está protegido por derechos de propiedad
          intelectual.{' '}
          <span className="text-white" data-id="element-584">No se permite su reproducción</span>{' '}
          total o parcial sin permiso previo y por escrito del titular.
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-585">
          4. Política de reembolsos
        </h3>
        <p data-id="element-586">
          Al tratarse de un{' '}
          <span className="text-white font-medium" data-id="element-587">
            producto digital de acceso inmediato
          </span>
          , no se aceptan devoluciones ni reembolsos una vez completada la
          descarga, salvo en los casos contemplados por la legislación vigente.
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-588">5. Modificaciones</h3>
        <p data-id="element-589">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones serán efectivas desde su publicación.
        </p>
      </LegalModal>

      <LegalModal isOpen={activeModal === 'cookies'} onClose={() => setActiveModal(null)} title="Política de Cookies" data-id="element-590">
        <p className="text-white font-semibold" data-id="element-591">
          Última actualización: junio 2025
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-592">
          1. ¿Qué son las cookies?
        </h3>
        <p data-id="element-593">
          Las cookies son pequeños archivos de texto que se almacenan en tu
          dispositivo al visitar un sitio web. Se utilizan para recordar
          preferencias y mejorar la experiencia de navegación.
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-594">
          2. Cookies que utilizamos
        </h3>
        <p data-id="element-595">
          <span className="text-white" data-id="element-596">Cookies esenciales:</span> necesarias
          para el funcionamiento básico del sitio y el proceso de compra
          (Shopify).
        </p>
        <p data-id="element-597">
          <span className="text-white" data-id="element-598">Cookies analíticas:</span> utilizamos
          Meta Pixel para medir el rendimiento de nuestras campañas
          publicitarias. Estos datos son anónimos y agregados.
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-599">3. Gestión de cookies</h3>
        <p data-id="element-600">
          Puedes configurar tu navegador para rechazar o eliminar cookies en
          cualquier momento. Ten en cuenta que desactivar ciertas cookies puede
          afectar la funcionalidad del sitio.
        </p>
        <h3 className="text-white font-semibold mt-2" data-id="element-601">4. Más información</h3>
        <p data-id="element-602">
          Para cualquier consulta sobre nuestra política de cookies, puedes
          contactarnos a través de nuestro email de soporte.
        </p>
      </LegalModal>
    </div>;
}